import React, { useEffect, useRef } from 'react'
import { connect } from 'dva'
import { watch } from '@arcgis/core/core/watchUtils.js'
import PropTypes from 'prop-types'
import WebTileLayer from '@arcgis/core/layers/WebTileLayer'

import { BASEMAPS, MAP_LAYER_IDS, MAP_MODES } from '../../../lib/constants'
import ImageryCaptureDate from './ImageryCaptureDate'
import ImageryControls from './ImageryControls'
import ImageryHeader from './ImageryHeader'
import InaccessibleData from './NoImageryData/inaccessible'
import Loader from '../../Loader'
import NonExistentData from './NoImageryData/nonExistent'
import SidebarBody from '../SidebarBody'
import SidebarDetailHeader from '../SidebarDetailHeader'
import styles from './HistoricalImagery.module.scss'
import ghAnalytics from '../../../lib/analytics'

export function HistoricalImagery(props) {
  const currentLayer = useRef(WebTileLayer)
  const {
    captureDate,
    dispatch,
    filteredList,
    isChangingBasemap,
    isChangingMapMode,
    isDataInaccessible,
    isDataNonExistent,
    isDisabled,
    isLoading,
    isNavigatingMap,
    isRefreshing,
    isTopographicBasemap,
    isZoomLevelInRange,
    loaderText,
    selectedLayer,
    stepper,
    viewRef,
    watchHandle
  } = props

  useEffect(() => {
    handleOnLoad()
    ghAnalytics.logHistoricalImageryLoaded() // For Analytics
  }, [])

  useEffect(() => {
    if (isChangingBasemap) {
      handleMapChange('basemap')
    } else if (isChangingMapMode) {
      handleMapChange('mapMode')
    }
  }, [isChangingBasemap, isChangingMapMode])

  const handleCurrentItem = item => {
    if (item) {
      dispatch({
        type: 'historicalImagery/setSelectedItem',
        payload: item
      })
      dispatch({
        type: 'historicalImagery/setNewLayers',
        payload: handleCurrentLayer(item),
        callback: () => {
          handleCaptureDate(item)
        }
      })
    }
  }

  const handleCaptureDate = item => {
    const {
      center,
      center: { spatialReference },
      zoom
    } = viewRef
    dispatch({
      type: 'historicalImagery/getMapCaptureDate',
      payload: {
        geometry: {
          spatialReference: {
            latestWkid: spatialReference.latestWkid,
            wkid: spatialReference.wkid
          },
          x: center.x,
          y: center.y
        },
        item: item,
        zoom: +zoom
      }
    })
  }

  const handleCurrentLayer = item => {
    if (!isTopographicBasemap) {
      if (item) {
        currentLayer.current = new WebTileLayer({
          id: MAP_LAYER_IDS.historical,
          urlTemplate: item.itemURL
        })
      } else if (selectedLayer) {
        currentLayer.current = selectedLayer
      } else if (filteredList.length > 0) {
        currentLayer.current = new WebTileLayer({
          id: MAP_LAYER_IDS.historical,
          urlTemplate: filteredList[filteredList.length - 1].itemURL
        })
      }
    }
    return currentLayer.current
  }

  const handleMapInteraction = navigating => {
    if (navigating) {
      dispatch({
        type: 'historicalImagery/setIsNavigatingMap',
        payload: true
      })
      handlePopup()
    } else {
      const { center, zoom } = viewRef
      dispatch({
        type: 'historicalImagery/setIsNavigatingMap',
        payload: false
      })
      dispatch({
        type: 'historicalImagery/setIsLoading',
        payload: true
      })
      dispatch({
        type: 'historicalImagery/getMapChanges',
        payload: {
          latitude: center.latitude,
          longitude: center.longitude,
          zoom: +zoom
        },
        callback: newCurrentItem => {
          handleCurrentItem(newCurrentItem)
          dispatch({
            type: 'historicalImagery/setIsLoading',
            payload: false
          })
        }
      })
    }
  }

  const handleMapChange = type => {
    switch (type) {
      case 'basemap':
        handleReset('basemapChange')
        break
      case 'mapMode':
        handleReset('mapModeChange')
        break
    }
  }

  const handleOnLoad = () => {
    if (!isDataInaccessible) {
      dispatch({
        type: 'historicalImagery/setIsLoading',
        payload: true
      })
      dispatch({
        type: 'historicalImagery/setNewLayers',
        payload: handleCurrentLayer(),
        callback: () => {
          const { center, zoom } = viewRef
          dispatch({
            type: 'historicalImagery/getMapChanges',
            payload: {
              latitude: center.latitude,
              longitude: center.longitude,
              zoom: +zoom
            },
            callback: newCurrentItem => {
              handleCurrentItem(newCurrentItem)
              if (!watchHandle) {
                handleWatchHandle('add')
              }
              dispatch({
                type: 'historicalImagery/setIsLoading',
                payload: false
              })
            }
          })
        }
      })
    }
  }

  const handlePopup = () => {
    const isVisible = viewRef.popup.visible
    if (isVisible) viewRef.popup.visible = false
  }

  const handleRefreshClick = () => {
    handleReset('refreshImagery')
  }

  const handleReset = type => {
    const isRefreshingImagery = type === 'refreshImagery'
    const dispatchLoaderType = isRefreshingImagery ? 'setIsRefreshing' : 'setIsLoading'
    dispatch({
      type: `historicalImagery/${dispatchLoaderType}`,
      payload: true
    })
    dispatch({
      type: 'historicalImagery/removeLayers',
      payload: { removeWatchHandle: isTopographicBasemap },
      callback: () => {
        if (!isDataInaccessible) {
          if (!watchHandle) {
            handleWatchHandle('add')
          }
          dispatch({
            type: 'historicalImagery/setNewLayers',
            payload: handleCurrentLayer(),
            callback: () => {
              const { center, zoom } = viewRef
              dispatch({
                type: 'historicalImagery/getMapChanges',
                payload: {
                  latitude: center.latitude,
                  longitude: center.longitude,
                  zoom: +zoom
                },
                callback: newCurrentItem => {
                  if (isRefreshingImagery) {
                    newCurrentItem = filteredList[filteredList.length - 1]
                    handleStepper(filteredList.length - 1)
                  }
                  handleCurrentItem(newCurrentItem)
                  dispatch({
                    type: `historicalImagery/${dispatchLoaderType}`,
                    payload: false
                  })
                }
              })
            }
          })
        } else {
          dispatch({
            type: `historicalImagery/${dispatchLoaderType}`,
            payload: false
          })
        }
      }
    })
  }

  const handleStepper = (value, values) => {
    let stepperPayload = { value }
    if (values) {
      stepperPayload = { value, values }
    }
    dispatch({
      type: 'historicalImagery/setStepper',
      payload: stepperPayload
    })
  }

  const handleStepperClick = value => {
    let stepperValue = value
    if (value > stepper.values.length - 1) {
      stepperValue = 0
    } else if (value < 0) {
      stepperValue = stepper.values.length
    }
    handleCurrentItem(filteredList[stepperValue])
    handlePopup()
    handleStepper(stepperValue)
  }

  const handleWatchHandle = (action = 'add') => {
    if (action === 'add') {
      const newWatchHandle = watch(viewRef, 'navigating', handleMapInteraction)
      dispatch({
        type: 'historicalImagery/setWatchHandle',
        payload: newWatchHandle
      })
    } else if (action === 'remove') {
      dispatch({
        type: 'historicalImagery/setWatchHandle',
        payload: null
      })
    }
  }

  return (
    <>
      <SidebarDetailHeader title='Historical Imagery' />
      <ImageryHeader isDisabled={isDisabled} isRefreshing={isRefreshing} handleRefreshClick={handleRefreshClick} />
      {isZoomLevelInRange && !isDataInaccessible && [isLoading, isNavigatingMap].includes(true) ? (
        <div className={styles.loader}>
          <Loader active={true} />
          {loaderText}
        </div>
      ) : (
        <>
          <SidebarBody className={styles.imageryContainer}>
            {isDataInaccessible ? (
              <InaccessibleData />
            ) : isDataNonExistent ? (
              <NonExistentData />
            ) : (
              <div className={styles.imageryMain}>
                <ImageryControls stepper={stepper} handleStepperClick={handleStepperClick} />
                <ImageryCaptureDate date={captureDate} />
              </div>
            )}
          </SidebarBody>
        </>
      )}
    </>
  )
}

HistoricalImagery.propTypes = {
  captureDate: PropTypes.string,
  filteredList: PropTypes.array,
  isChangingBasemap: PropTypes.bool,
  isChangingMapMode: PropTypes.bool,
  isDataInaccessible: PropTypes.bool,
  isDataNonExistent: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isHybridBasemap: PropTypes.bool,
  isLoading: PropTypes.bool,
  isNavigatingMap: PropTypes.bool,
  isRefreshing: PropTypes.bool,
  isTopographicBasemap: PropTypes.bool,
  isZoomLevelInRange: PropTypes.bool,
  loaderText: PropTypes.string,
  selectedLayer: PropTypes.any,
  stepper: PropTypes.object,
  viewRef: PropTypes.any,
  watchHandle: PropTypes.any
}

export default connect(model => {
  const { basemapMode, isChangingBasemap, isChangingMapMode, mapMode, mapViewRef, sceneViewRef, zoomLevel } = model.map
  const { captureDate, filteredList, is3DRestricted, isLoading, isNavigatingMap, isRefreshing, selectedLayer, stepper, watchHandle } = model.historicalImagery
  const isTwoDMap = mapMode === MAP_MODES.TwoD
  const isHybridBasemap = basemapMode === BASEMAPS.Hybrid
  const isTopographicBasemap = basemapMode === BASEMAPS.Topographic
  const isRestricting3D = is3DRestricted && !isTwoDMap
  const isDataInaccessible = isRestricting3D || isTopographicBasemap
  const isDataNonExistent = !filteredList.length
  const isDisabled = isDataInaccessible || isDataNonExistent || isLoading || isRefreshing
  const isZoomLevelInRange = +zoomLevel < 20
  const loaderText = isRefreshing
    ? 'Refreshing imagery...'
    : isChangingBasemap
    ? 'Changing basemap...'
    : isNavigatingMap
    ? 'Navigating map...'
    : 'Loading imagery...'
  const viewRef = isTwoDMap ? mapViewRef : sceneViewRef

  return {
    captureDate,
    filteredList,
    isChangingBasemap,
    isChangingMapMode,
    isDataInaccessible,
    isDataNonExistent,
    isDisabled,
    isHybridBasemap,
    isLoading,
    isNavigatingMap,
    isRefreshing,
    isTopographicBasemap,
    isZoomLevelInRange,
    loaderText,
    selectedLayer,
    stepper,
    viewRef,
    watchHandle
  }
})(HistoricalImagery)
